import { useDatoClient } from '@src/stores-v2/StoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useRef, useState } from 'react';

const logger = LogManager.getLogger('components:usePromptChannelSlugs');

const EMPTY_SLUGS: ReadonlyMap<string, string> = new Map();

/**
 * Prompt id -> distribution channel slug for every AI prompt that declares a
 * channel (PLAT-11038).
 *
 * The prompts shown in the producer modal come from the customer-type query,
 * which uses the base prompt fragment and so cannot carry the channel link
 * (adding fields there breaks the story query pre-migration). This fetches the
 * association separately, through the fallback-protected prompt query.
 *
 * Fetched once, the first time `enabled` turns true (the producer modal
 * opening), so a Content Studio session that never generates pays nothing. On
 * failure the map stays empty and every post falls back to the default preview.
 */
export function usePromptChannelSlugs(
  enabled: boolean,
): ReadonlyMap<string, string> {
  const datoClientStore = useDatoClient();
  const [slugsByPromptId, setSlugsByPromptId] =
    useState<ReadonlyMap<string, string>>(EMPTY_SLUGS);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    let cancelled = false;
    datoClientStore.aiPromptRepository
      .getPromptChannelSlugs()
      .then(slugs => {
        if (!cancelled) setSlugsByPromptId(slugs);
      })
      .catch(error => {
        // Non-fatal: previews fall back to the default channel.
        logger.warn(
          'Failed to resolve prompt distribution channels',
          extractErrorInfo(error),
        );
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, datoClientStore]);

  return slugsByPromptId;
}
